import React from 'react';
import { CloseOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';

import type { AuthProps } from '../../auth';
import { useAuthDecision } from '../../auth';
import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { cx, defineRecipe, useRecipe, type Extensible, type Styleable } from '../../variants';

/**
 * `mapped` — a live value. `stale` — the value no longer exists upstream, so
 * it is struck through and flagged. `unmapped` — the value is valid but has no
 * link yet (amber, not struck through — distinct from `stale`, whose link
 * exists but is broken). `add` — the dashed "+" affordance.
 *
 * `Extensible`: an app may add a tone via `UIVariantsProvider` without editing
 * this union.
 */
export type ChipTone = Extensible<'mapped' | 'stale' | 'unmapped' | 'add'>;

/**
 * Three tones × interactive × denied × selected. `denied` and `selected` are
 * their own axes rather than folded into `tone`: `denied` can apply to ANY
 * tone (folding it in would have meant nine members that mostly repeat each
 * other), and `selected` is a toggle state orthogonal to tone entirely — a
 * filter pill needs "mapped, selected" and "mapped, unselected" to be the
 * same tone with a different pressed state, not two tones.
 */
export const chipRecipe = defineRecipe('chip', 'ui-chip', {
    variants: {
        tone: {
            mapped: 'ui-chip--mapped',
            stale: 'ui-chip--stale',
            unmapped: 'ui-chip--unmapped',
            add: 'ui-chip--add',
        },
        // `yes`/`no` rather than CVA's boolean `true`/`false` keys: those make
        // the axis a real boolean, and the override registry is string-keyed so
        // an app could not address them. One spelling everywhere beats two.
        interactive: { yes: 'ui-chip--interactive', no: '' },
        denied: { yes: 'ui-chip--denied', no: '' },
        selected: { yes: 'ui-chip--selected', no: '' },
    },
    defaultVariants: { tone: 'mapped', interactive: 'no', denied: 'no', selected: 'no' },
});

export interface ChipProps extends AuthProps, Styleable {
    children: React.ReactNode;
    tone?: ChipTone;
    /**
     * Toggle/selectable state for filter-pill usage. Left `undefined` (the
     * default), the chip renders exactly as before this axis existed — no
     * `aria-pressed` attribute, no selected styling. Only an explicit
     * `true`/`false` opts a chip into the toggle contract, because
     * `aria-pressed` must only be emitted on an element that is genuinely a
     * toggle button (and only once it is also `interactive`).
     */
    selected?: boolean;
    /** Renders the trailing ✕ and makes it actionable. */
    onRemove?: () => void;
    /** Whole-chip click — used by the `add` tone and by editable cells. */
    onClick?: () => void;
    /** Accessible name for the remove control; defaults to the shared catalog. */
    removeLabel?: string;
    /** Optional trailing count badge, rendered after `children` (e.g. a filter pill's match count). */
    count?: number | React.ReactNode;
    /**
     * EAA-1839: the `add`/`unmapped`/`stale` glyph (`+`/⚠), on by default so
     * every existing caller renders exactly as before. Set `false` for a plain
     * pill with no icon — e.g. a Level/role-key display chip whose colour
     * already carries the tone and where a glyph reads as noisier than
     * informative alongside a `Popover` that already spells the state out.
     */
    icon?: boolean;
    /**
     * EAA-1817: render the label in the body font instead of monospace.
     *
     * `Chip` is monospace because it was built to hold a machine VALUE — a role
     * key, an app-role key. The handoff's 2026-08-24 revision turns these chips
     * into display NAMES with the key moved into a tooltip ("dùng nhiều mã
     * quá"), and a Vietnamese phrase set in monospace reads as a code that
     * happens to have spaces in it. The box, the palette and the four tone pairs
     * are unchanged — only the typeface and size.
     *
     * Defaults to monospace, so every existing caller renders exactly as before.
     */
    mono?: boolean;
    /**
     * EAA-1817: native tooltip text, e.g. "Mã trong ứng dụng: teacher".
     *
     * A `title` rather than a `HoverCard`: this is a chip in a table cell whose
     * only job is to disclose the code behind the name, there can be a dozen of
     * them in one row, and mounting a popover per chip to show one line of text
     * costs a trigger and a portal each. `cursor: help` follows automatically,
     * the same pairing the design's own markup uses.
     */
    title?: string;
}

/**
 * EAA-1804: the component sheet's chip — the unit a role map is built from.
 *
 * Ant Design's `Tag` is the closest primitive but the wrong shape here: the
 * sheet's chip is monospace, uses an inset ring rather than a border, and has
 * a dashed "add" variant that must be **exactly the same height** as a real
 * chip. That last point is why the add affordance is this component and not a
 * button — when the last chip in a cell is removed, a differently-sized
 * placeholder makes the whole table row jump.
 *
 * Domain-free by construction: it renders whatever `children` it is handed and
 * knows nothing about role keys or applications.
 */
export function Chip({
    children,
    tone = 'mapped',
    selected,
    onRemove,
    onClick,
    removeLabel,
    count,
    auth,
    // `disable` matches every other interactive atomic in the kit.
    authFallback = 'disable',
    icon = true,
    mono = true,
    title,
    className,
    style,
}: ChipProps): React.ReactElement {
    const colors = useUIColors();
    const text = useUIText();
    const decision = useAuthDecision(auth, authFallback);

    // Resolved BEFORE the `hide` guard: hooks must run in the same order every
    // render, and this component returns early when auth denies it outright.
    const denied = decision !== 'allow';
    const interactive = Boolean(onClick) && !denied;
    const recipeClassName = useRecipe(chipRecipe, {
        tone,
        interactive: interactive ? 'yes' : 'no',
        denied: denied ? 'yes' : 'no',
        selected: selected ? 'yes' : 'no',
    });

    if (decision === 'hide') return <></>;

    const palettes: Record<string, { bg: string; ring: string; fg: string }> = {
        mapped: {
            bg: colors.chipOkBg,
            ring: colors.chipOkRing,
            fg: colors.chipOkText,
        },
        stale: {
            bg: colors.chipStaleBg,
            ring: colors.chipStaleRing,
            fg: colors.chipStaleText,
        },
        unmapped: {
            bg: colors.warningBg,
            ring: colors.warning,
            fg: colors.warningText,
        },
        add: {
            bg: 'transparent',
            ring: colors.controlRing,
            fg: colors.textSecondary,
        },
    };
    // An app-defined tone paints itself through the class; `mapped` keeps the
    // chip legible in the meantime rather than rendering it invisible.
    const palette = palettes[tone] ?? palettes.mapped;
    const isAdd = tone === 'add';
    const isStale = tone === 'stale';
    const isUnmapped = tone === 'unmapped';

    // `aria-pressed` only makes sense on a genuine toggle: a chip that is both
    // clickable and opted into the `selected` axis. A chip with `selected`
    // left `undefined` (every pre-existing caller) keeps rendering with no
    // `aria-pressed` attribute at all, matching prior markup byte-for-byte.
    const ariaPressed = interactive && selected !== undefined ? selected : undefined;

    return (
        <span
            className={cx(recipeClassName, className)}
            title={title}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={ariaPressed}
            onClick={interactive ? onClick : undefined}
            onKeyDown={
                interactive
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: onRemove ? 7 : 6,
                padding: '4px 10px',
                borderRadius: 6,
                background: palette.bg,
                // `add` is the one tone drawn with a real (dashed) border; the
                // others use an inset ring so every tone occupies one box size.
                border: isAdd ? `1px dashed ${palette.ring}` : '1px solid transparent',
                boxShadow: isAdd ? undefined : `inset 0 0 0 1px ${palette.ring}`,
                color: palette.fg,
                fontFamily: mono ? 'ui-monospace, Menlo, Consolas, monospace' : undefined,
                fontSize: mono ? 13 : 12.5,
                fontWeight: 500,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                // `help` when the chip only discloses something on hover; a
                // clickable chip is still a pointer, and an inert one is neither.
                cursor: interactive ? 'pointer' : title ? 'help' : 'default',
                opacity: denied ? 0.6 : 1,
                ...style,
            }}
        >
            {icon && isAdd && <PlusOutlined style={{ fontSize: 11 }} />}
            {icon && isUnmapped && <WarningOutlined style={{ fontSize: 12 }} />}
            {/* EAA-1839: `minWidth: 0` is load-bearing, not tidiness. This span is a
                flex item of the chip's own `inline-flex` root, and a flex item will
                not shrink below its content without it — so a caller that clamps a
                chip to a narrow column (a table cell) found the text refusing to
                shrink and painting straight through the chip's edge onto the next
                columns. Any ellipsis the caller sets on its own inner element could
                never engage, because this wrapper never got narrower than the text.
                Measured on the accounts board: +150px of spill past the cell before
                this, inside it after. Fixed here rather than by each caller reaching
                in with a `> span` descendant selector, which is what one of them had
                to do and which breaks the moment this markup changes. */}
            <span
                style={{
                    minWidth: 0,
                    textDecoration: isStale ? 'line-through' : undefined,
                }}
            >
                {children}
            </span>
            {icon && isStale && <WarningOutlined style={{ fontSize: 12 }} />}
            {count !== undefined && (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        opacity: 0.75,
                    }}
                >
                    {count}
                </span>
            )}
            {onRemove && !denied && (
                <button
                    type="button"
                    aria-label={removeLabel ?? text.common.delete}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        // EAA-1826: `inherit`, never `textLight`. This is an
                        // interactive control, so it gets no placeholder
                        // exemption — #94A1AD is 2.64:1 on white and 2.42:1 on
                        // the chip's own tint, both under WCAG 1.4.11's 3:1 for
                        // UI components. Inheriting takes the tone's own `fg`,
                        // which is contrast-checked against that tone's fill and
                        // keeps a stale chip's × red instead of dropping it to a
                        // grey that reads as a different control. De-emphasis
                        // against the label comes from the 11px size, not from
                        // lowered contrast.
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: 11,
                        lineHeight: 1,
                    }}
                >
                    <CloseOutlined />
                </button>
            )}
        </span>
    );
}

export default Chip;
